import { useState, useEffect, useRef } from 'react';
import { Plus, X, RefreshCw } from 'lucide-react';
import {
  useWebsiteArticles, useWebsiteArticle,
  useCreateWebsiteArticle, useUpdateWebsiteArticle,
  usePublishWebsiteArticle, useUnpublishWebsiteArticle,
  useDeleteWebsiteArticle, useUploadWebsiteArticleImage, useImportWebsiteArticleMarkdown,
} from '../../api/hooks';
import { SectionHeader, Spinner } from '../../components/ui';
import { cn, fmtDateTime, fmtDate } from '../../lib/utils';
import { toast } from '../../store';

const PAGE_SIZE = 50;
const ARTICLE_STATUSES = ['DRAFT', 'PUBLISHED', 'UNPUBLISHED', 'ARCHIVED'];

const STATUS_STYLE = {
  DRAFT:       'text-txt-muted bg-bg-surface border-bg-border',
  PUBLISHED:   'text-bull bg-bull/10 border-bull/30',
  UNPUBLISHED: 'text-warn bg-warn/10 border-warn/30',
  ARCHIVED:    'text-bear bg-bear/10 border-bear/30',
};

const EMPTY_FORM = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  featured_image_url: '',
  featured_image_alt: '',
  og_image_url: '',
  meta_title: '',
  meta_description: '',
  canonical_url: '',
  category: '',
  tags: '',
  author_name: '',
};

function articleToForm(article) {
  return {
    title:              article.title              ?? '',
    slug:               article.slug               ?? '',
    excerpt:            article.excerpt             ?? '',
    body:               article.body               ?? '',
    featured_image_url: article.featured_image_url ?? '',
    featured_image_alt: article.featured_image_alt ?? '',
    og_image_url:       article.og_image_url       ?? '',
    meta_title:         article.meta_title          ?? '',
    meta_description:   article.meta_description   ?? '',
    canonical_url:      article.canonical_url       ?? '',
    category:           article.category            ?? '',
    tags:               Array.isArray(article.tags) ? article.tags.join(', ') : (article.tags ?? ''),
    author_name:        article.author_name         ?? '',
  };
}

function formToPayload(form) {
  const ns = (v) => v.trim() || null;
  const slugVal = form.slug.trim();
  return {
    title:              form.title.trim(),
    body:               form.body,
    ...(slugVal ? { slug: slugVal } : {}),
    excerpt:            ns(form.excerpt),
    featured_image_url: ns(form.featured_image_url),
    featured_image_alt: ns(form.featured_image_alt),
    og_image_url:       ns(form.og_image_url),
    meta_title:         ns(form.meta_title),
    meta_description:   ns(form.meta_description),
    canonical_url:      ns(form.canonical_url),
    category:           ns(form.category),
    ...(form.author_name.trim() ? { author_name: form.author_name.trim() } : {}),
    tags:               form.tags.trim()
      ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : [],
  };
}

// Uploaded images may be relative (e.g. /assets/blog/file.webp) — that's what gets persisted
// and served by the static site. Only the preview <img> needs an absolute src, resolved against
// the website origin (not the API origin).
function imagePreviewSrc(url) {
  if (!url) return '';
  return url.startsWith('/') ? `https://algosys.co.in${url}` : url;
}

function StatusBadge({ status }) {
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono',
      STATUS_STYLE[status] ?? 'text-txt-muted border-bg-border',
    )}>
      {status ?? '—'}
    </span>
  );
}

function Field({ label, children, span2 = false, hint }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <label className="block text-txt-muted mb-1">{label}</label>
      {children}
      {hint && <p className="mt-0.5 text-2xs text-txt-muted">{hint}</p>}
    </div>
  );
}

function ToolbarBtn({ label, title, onClick, className }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn('px-1.5 py-0.5 text-2xs rounded hover:bg-bg-hover text-txt-muted hover:text-txt-primary transition-colors font-mono', className)}
    >
      {label}
    </button>
  );
}

function renderMarkdownPreview(md) {
  if (!md) return '';

  function enc(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function safeHref(href) {
    const h = String(href).trim();
    if (/^https?:\/\//i.test(h)) return h;
    if (h.startsWith('/') && !h.startsWith('//')) return h;
    if (/^mailto:[^<>"'\s]+$/i.test(h)) return h;
    return null;
  }

  function inlineFmt(s) {
    s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    s = s.replace(/`([^`]+)`/g, '<code style="background:rgba(0,0,0,0.25);padding:1px 5px;border-radius:3px;font-family:monospace;font-size:0.875em">$1</code>');
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, text, href) => {
      const rawHref = href.replace(/&amp;/g, '&');
      const safe = safeHref(rawHref);
      if (!safe) return text;
      const isExternal = /^https?:\/\//i.test(safe);
      const attrs = isExternal ? ' target="_blank" rel="noopener noreferrer"' : '';
      return `<a href="${enc(safe)}"${attrs} style="color:#3b82f6;text-decoration:underline">${text}</a>`;
    });
    return s;
  }

  // Extract fenced code blocks before line processing
  const codeBlocks = [];
  const withPlaceholders = md.replace(/```[^\n]*\n([\s\S]*?)```/g, (_, code) => {
    codeBlocks.push(
      `<pre style="background:rgba(0,0,0,0.25);border-radius:6px;padding:14px;overflow-x:auto;margin:0 0 14px;font-family:monospace;font-size:0.8em"><code>${enc(code)}</code></pre>`,
    );
    return `\x00CODE${codeBlocks.length - 1}\x00`;
  });

  const lines = withPlaceholders.split('\n');
  const out = [];
  let inUl = false;
  let inOl = false;

  function closeList() {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  }

  for (const rawLine of lines) {
    const codeMatch = rawLine.trim().match(/^\x00CODE(\d+)\x00$/);
    if (codeMatch) {
      closeList();
      out.push(codeBlocks[parseInt(codeMatch[1], 10)]);
      continue;
    }

    let m;
    if ((m = rawLine.match(/^#{4}\s+(.*)/))) { closeList(); out.push(`<h4 style="font-size:1em;font-weight:700;margin:20px 0 8px;color:inherit">${inlineFmt(enc(m[1]))}</h4>`); continue; }
    if ((m = rawLine.match(/^#{3}\s+(.*)/))) { closeList(); out.push(`<h3 style="font-size:1.1em;font-weight:700;margin:20px 0 8px;color:inherit">${inlineFmt(enc(m[1]))}</h3>`); continue; }
    if ((m = rawLine.match(/^#{2}\s+(.*)/))) { closeList(); out.push(`<h2 style="font-size:1.2em;font-weight:700;margin:24px 0 10px;color:inherit">${inlineFmt(enc(m[1]))}</h2>`); continue; }
    if ((m = rawLine.match(/^#\s+(.*)/)))    { closeList(); out.push(`<h1 style="font-size:1.35em;font-weight:700;margin:28px 0 12px;color:inherit">${inlineFmt(enc(m[1]))}</h1>`); continue; }

    if ((m = rawLine.match(/^>\s?(.*)/))) {
      closeList();
      out.push(`<blockquote style="border-left:3px solid rgba(255,255,255,0.2);margin:0 0 10px;padding:6px 0 6px 16px;opacity:0.7;font-style:italic">${inlineFmt(enc(m[1]))}</blockquote>`);
      continue;
    }

    if ((m = rawLine.match(/^[-*+]\s+(.*)/))) {
      if (inOl) { out.push('</ol>'); inOl = false; }
      if (!inUl) { out.push('<ul style="margin:0 0 14px;padding-left:24px">'); inUl = true; }
      out.push(`<li style="margin-bottom:4px">${inlineFmt(enc(m[1]))}</li>`);
      continue;
    }

    if ((m = rawLine.match(/^(\d+)\.\s+(.*)/))) {
      if (inUl) { out.push('</ul>'); inUl = false; }
      if (!inOl) { out.push('<ol style="margin:0 0 14px;padding-left:24px">'); inOl = true; }
      out.push(`<li style="margin-bottom:4px">${inlineFmt(enc(m[2]))}</li>`);
      continue;
    }

    if (!rawLine.trim()) {
      closeList();
      out.push('<div style="height:0.6em"></div>');
      continue;
    }

    closeList();
    out.push(`<p style="margin:0 0 10px;line-height:1.7">${inlineFmt(enc(rawLine))}</p>`);
  }

  closeList();
  return out.join('');
}

const MD_OVERWRITE_CHECK_FIELDS = [
  'title', 'slug', 'body', 'excerpt', 'meta_title', 'meta_description',
  'category', 'tags', 'featured_image_url', 'og_image_url',
];

function ArticleDrawer({ articleId, onClose, onCreated, onDeleted }) {
  const isNew = articleId === 'new';
  const { data: article, isLoading: detailLoading } = useWebsiteArticle(isNew ? null : articleId);

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [initialized, setInitialized] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const bodyRef = useRef(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteArticle = useDeleteWebsiteArticle();

  const [uploadingImage, setUploadingImage] = useState(null); // null | 'featured' | 'og'
  const [imageUploadMeta, setImageUploadMeta] = useState({});
  const [imageUploadWarning, setImageUploadWarning] = useState({});
  const featuredImgRef = useRef(null);
  const ogImgRef = useRef(null);
  const uploadArticleImage = useUploadWebsiteArticleImage();

  const [confirmMdFile, setConfirmMdFile] = useState(null);
  const [mdWarnings, setMdWarnings] = useState([]);
  const mdImportRef = useRef(null);
  const importMarkdown = useImportWebsiteArticleMarkdown();

  useEffect(() => {
    setInitialized(false);
    setConfirmAction(null);
    setPreview(false);
    setConfirmDelete(false);
    setImageUploadMeta({});
    setImageUploadWarning({});
    setConfirmMdFile(null);
    setMdWarnings([]);
    if (isNew) setForm({ ...EMPTY_FORM });
  }, [articleId, isNew]);

  function insertMd(before, after, placeholder) {
    const el = bodyRef.current;
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const sel = form.body.slice(s, e);
    const ins = sel ? `${before}${sel}${after}` : `${before}${placeholder}${after}`;
    set('body', form.body.slice(0, s) + ins + form.body.slice(e));
    requestAnimationFrame(() => {
      el.focus();
      const cur = sel ? s + ins.length : s + before.length + placeholder.length;
      el.setSelectionRange(cur, cur);
    });
  }

  function insertHeading(prefix, placeholder) {
    const el = bodyRef.current;
    if (!el) return;
    const start     = el.selectionStart;
    const lineStart = form.body.lastIndexOf('\n', start - 1) + 1;
    const lineEnd   = form.body.indexOf('\n', start);
    const end       = lineEnd === -1 ? form.body.length : lineEnd;
    const lineText  = form.body.slice(lineStart, end).replace(/^#{1,6}\s*/, '');
    const newLine   = lineText ? `${prefix} ${lineText}` : `${prefix} ${placeholder}`;
    set('body', form.body.slice(0, lineStart) + newLine + form.body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = lineStart + newLine.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function insertCode() {
    const el = bodyRef.current;
    if (!el) return;
    const sel = form.body.slice(el.selectionStart, el.selectionEnd);
    if (sel.includes('\n')) {
      insertMd('```\n', '\n```', 'code');
    } else {
      insertMd('`', '`', 'code');
    }
  }

  useEffect(() => {
    if (!isNew && article && !initialized) {
      setForm(articleToForm(article));
      setInitialized(true);
    }
  }, [article, isNew, initialized]);

  const createArticle   = useCreateWebsiteArticle();
  const updateArticle   = useUpdateWebsiteArticle();
  const publishArticle  = usePublishWebsiteArticle();
  const unpublishArticle = useUnpublishWebsiteArticle();

  function set(key, val) { setForm((f) => ({ ...f, [key]: val })); }

  async function handleSave() {
    if (!form.title.trim()) { toast.warn('Title is required'); return; }
    if (!form.body.trim())  { toast.warn('Body is required'); return; }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (isNew) {
        const created = await createArticle.mutateAsync(payload);
        toast.success('Article created');
        onCreated?.(created);
      } else {
        await updateArticle.mutateAsync({ id: articleId, payload });
        toast.success('Article saved');
      }
    } catch (err) {
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirm() {
    const action = confirmAction;
    setConfirmAction(null);
    try {
      if (action === 'publish') {
        await updateArticle.mutateAsync({ id: articleId, payload: formToPayload(form) });
        await publishArticle.mutateAsync(articleId);
        toast.success('Article published');
      } else {
        await unpublishArticle.mutateAsync(articleId);
        toast.success('Article unpublished');
      }
    } catch (err) {
      toast.error(err?.message || `Failed to ${action} article`);
    }
  }

  async function handleDelete() {
    setConfirmDelete(false);
    try {
      await deleteArticle.mutateAsync(articleId);
      toast.success('Article deleted');
      onDeleted?.(articleId);
    } catch (err) {
      toast.error(err?.message || 'Delete failed');
    }
  }

  async function handleImageUpload(file, imageType) {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.warn('Only JPG, PNG, and WebP images are allowed');
      return;
    }
    setUploadingImage(imageType);
    try {
      const res = await uploadArticleImage.mutateAsync({ file, imageType });
      set(imageType === 'featured' ? 'featured_image_url' : 'og_image_url', res.url);
      setImageUploadMeta((prev) => ({ ...prev, [imageType]: res }));
      setImageUploadWarning((prev) => ({ ...prev, [imageType]: res.deploy_warning || null }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploadingImage(null);
      const ref = imageType === 'featured' ? featuredImgRef : ogImgRef;
      if (ref.current) ref.current.value = '';
    }
  }

  function handleMdFileSelected(file) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.md')) {
      toast.warn('Only .md files are accepted');
      return;
    }
    if (mdImportRef.current) mdImportRef.current.value = '';
    const hasContent = MD_OVERWRITE_CHECK_FIELDS.some((key) => form[key]?.trim());
    if (hasContent) {
      setConfirmMdFile(file);
    } else {
      doMdImport(file);
    }
  }

  async function doMdImport(file) {
    try {
      const res = await importMarkdown.mutateAsync(file);
      setForm((f) => ({
        ...f,
        title:            res.title            || f.title,
        slug:             res.slug             || f.slug,
        body:             res.body             || f.body,
        meta_title:       res.meta_title       || f.meta_title,
        meta_description: res.meta_description || f.meta_description,
        excerpt:          res.excerpt          || f.excerpt,
        category:         res.category         || f.category,
        tags:             Array.isArray(res.tags) ? res.tags.join(', ') : f.tags,
      }));
      setMdWarnings(res.warnings || []);
      toast.success('Markdown imported — review fields before saving');
    } catch (err) {
      toast.error(err?.message || 'Markdown import failed');
    }
  }

  const status      = article?.status ?? null;
  const canPublish   = !isNew && (status === 'DRAFT' || status === 'UNPUBLISHED');
  const canUnpublish = !isNew && status === 'PUBLISHED';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full md:max-w-[min(860px,75vw)] bg-bg-panel border-l border-bg-border flex flex-col animate-slide-in shadow-modal md:self-start md:my-6 md:max-h-[calc(100vh-48px)] overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-3.5 border-b border-bg-border shrink-0 gap-3">
          <div className="min-w-0">
            <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-0.5">
              Website Articles
            </p>
            <h3 className="font-display font-semibold text-sm text-txt-primary truncate">
              {isNew ? 'New Article' : (article?.title || '…')}
            </h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isNew && status && <StatusBadge status={status} />}
            {canPublish && (
              <button
                onClick={() => setConfirmAction('publish')}
                className="btn-ghost text-xs text-bull border border-bull/30 hover:bg-bull/10 px-3 py-1.5 rounded-md"
              >
                Publish
              </button>
            )}
            {canUnpublish && (
              <button
                onClick={() => setConfirmAction('unpublish')}
                className="btn-ghost text-xs text-warn border border-warn/30 hover:bg-warn/10 px-3 py-1.5 rounded-md"
              >
                Unpublish
              </button>
            )}
            <button type="button"
              onClick={() => mdImportRef.current?.click()}
              className="btn-ghost text-xs border border-bg-border px-3 py-1.5 rounded-md"
            >
              Import .md
            </button>
            <input ref={mdImportRef} type="file" accept=".md,text/markdown" className="hidden"
              onChange={(e) => handleMdFileSelected(e.target.files?.[0])} />
            {!isNew && (
              <button
                onClick={() => { setConfirmAction(null); setConfirmDelete(true); }}
                className="btn-ghost text-xs text-bear border border-bear/30 hover:bg-bear/10 px-3 py-1.5 rounded-md"
              >
                Delete
              </button>
            )}
            <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg">
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Delete confirmation */}
        {confirmDelete && (
          <div className="px-5 py-3 bg-bear/5 border-b border-bear/20 flex items-center gap-3 shrink-0">
            <span className="text-xs text-txt-primary flex-1">
              This will permanently delete the article and remove it from the public blog after the static deploy runs.
            </span>
            <button onClick={handleDelete}
              className="text-xs px-3 py-1.5 rounded-md border text-bear border-bear/30 hover:bg-bear/10">
              Confirm Delete
            </button>
            <button onClick={() => setConfirmDelete(false)}
              className="text-xs text-txt-muted hover:text-txt-secondary px-2 py-1.5">
              Cancel
            </button>
          </div>
        )}

        {/* Markdown import confirmation */}
        {confirmMdFile && (
          <div className="px-5 py-3 bg-warn/5 border-b border-warn/20 flex items-center gap-3 shrink-0">
            <span className="text-xs text-txt-primary flex-1">
              Importing Markdown will replace the current form fields. Continue?
            </span>
            <button onClick={() => { const f = confirmMdFile; setConfirmMdFile(null); doMdImport(f); }}
              className="text-xs px-3 py-1.5 rounded-md border text-warn border-warn/30 hover:bg-warn/10">
              Yes, import
            </button>
            <button onClick={() => setConfirmMdFile(null)}
              className="text-xs text-txt-muted hover:text-txt-secondary px-2 py-1.5">
              Cancel
            </button>
          </div>
        )}

        {/* Inline confirmation */}
        {confirmAction && (
          <div className="px-5 py-3 bg-warn/5 border-b border-warn/20 flex items-center gap-3 shrink-0">
            <span className="text-xs text-txt-primary flex-1">
              {confirmAction === 'publish'
                ? 'Publish this article? It will be publicly visible on the website.'
                : 'Unpublish this article? It will no longer be visible on the website.'}
            </span>
            <button
              onClick={handleConfirm}
              className={cn(
                'text-xs px-3 py-1.5 rounded-md border transition-colors',
                confirmAction === 'publish'
                  ? 'text-bull border-bull/30 hover:bg-bull/10'
                  : 'text-warn border-warn/30 hover:bg-warn/10',
              )}
            >
              Confirm {confirmAction === 'publish' ? 'Publish' : 'Unpublish'}
            </button>
            <button
              onClick={() => setConfirmAction(null)}
              className="text-xs text-txt-muted hover:text-txt-secondary px-2 py-1.5"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {!isNew && detailLoading && !initialized ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs">

              {mdWarnings.length > 0 && (
                <div className="col-span-2 bg-warn/5 border border-warn/20 rounded-md px-3 py-2">
                  <p className="text-2xs font-mono uppercase text-warn mb-1">Import warnings</p>
                  {mdWarnings.map((w, i) => (
                    <p key={i} className="text-2xs text-warn">{w}</p>
                  ))}
                  <button type="button" onClick={() => setMdWarnings([])}
                    className="text-2xs text-txt-muted hover:text-txt-secondary mt-1 block">
                    Dismiss
                  </button>
                </div>
              )}

              <Field label="Title *" span2>
                <input
                  className="input-base w-full"
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  placeholder="Article title"
                />
              </Field>

              <Field label="Slug" span2 hint="Leave blank to auto-generate from title.">
                <input
                  className="input-base w-full font-mono"
                  value={form.slug}
                  onChange={(e) => set('slug', e.target.value)}
                  placeholder="my-article-slug"
                />
              </Field>

              <Field label="Excerpt" span2>
                <textarea
                  className="input-base w-full resize-none"
                  rows={2}
                  value={form.excerpt}
                  onChange={(e) => set('excerpt', e.target.value)}
                  placeholder="Short description for listings and SEO"
                />
              </Field>

              {/* Body field with Markdown toolbar and preview */}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-txt-muted">Body (Markdown) *</label>
                  <div className="flex gap-0.5">
                    <button
                      type="button"
                      onClick={() => setPreview(false)}
                      className={cn(
                        'text-2xs px-2 py-0.5 rounded border transition-colors',
                        !preview
                          ? 'border-bg-border bg-bg-surface text-txt-primary'
                          : 'border-transparent text-txt-muted hover:text-txt-secondary',
                      )}
                    >
                      Write
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreview(true)}
                      className={cn(
                        'text-2xs px-2 py-0.5 rounded border transition-colors',
                        preview
                          ? 'border-bg-border bg-bg-surface text-txt-primary'
                          : 'border-transparent text-txt-muted hover:text-txt-secondary',
                      )}
                    >
                      Preview
                    </button>
                  </div>
                </div>

                {!preview && (
                  <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border border-bg-border rounded-t-md bg-bg-surface">
                    <ToolbarBtn label="H2"  title="Heading 2 (##)"    onClick={() => insertHeading('##',   'Heading')}      />
                    <ToolbarBtn label="H3"  title="Heading 3 (###)"   onClick={() => insertHeading('###',  'Sub heading')}   />
                    <ToolbarBtn label="H4"  title="Heading 4 (####)"  onClick={() => insertHeading('####', 'Small heading')} />
                    <span className="w-px h-3.5 bg-bg-border mx-0.5" />
                    <ToolbarBtn label="B"    title="Bold (**text**)"    className="font-bold" onClick={() => insertMd('**', '**', 'bold text')}           />
                    <ToolbarBtn label="I"    title="Italic (*text*)"    className="italic"    onClick={() => insertMd('*',  '*',  'italic text')}          />
                    <ToolbarBtn label="Link" title="Link ([text](url))"               onClick={() => insertMd('[', '](https://example.com)', 'link text')} />
                    <span className="w-px h-3.5 bg-bg-border mx-0.5" />
                    <ToolbarBtn label="•"   title="Bullet list (- item)"   onClick={() => insertMd('\n- ',   '', 'item')} />
                    <ToolbarBtn label="1."  title="Numbered list (1. item)" onClick={() => insertMd('\n1. ', '', 'item')} />
                    <ToolbarBtn label="❝"  title="Blockquote (> text)"     onClick={() => insertMd('\n> ',  '', 'note')} />
                    <ToolbarBtn label="</>" title="Code (`code` or ```block```)"      onClick={insertCode}                />
                  </div>
                )}

                {preview ? (
                  <div
                    className="input-base w-full min-h-[320px] overflow-y-auto"
                    style={{ fontSize: '13px', lineHeight: '1.6', fontFamily: 'inherit' }}
                    dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(form.body) }}
                  />
                ) : (
                  <textarea
                    ref={bodyRef}
                    className="input-base w-full font-mono resize-y !h-auto min-h-[320px] rounded-t-none border-t-0"
                    value={form.body}
                    onChange={(e) => set('body', e.target.value)}
                    placeholder="Write article content in Markdown…"
                    spellCheck={false}
                  />
                )}

                <p className="mt-0.5 text-2xs text-txt-muted">
                  {'## H2  ### H3  **bold**  *italic*  [text](/platform.html)  - bullet  1. item  > quote  `code`  — Preview is approximate. Final public article rendering is produced by the static blog generator.'}
                </p>
              </div>

              <Field label="Category">
                <input
                  className="input-base w-full"
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)}
                  placeholder="e.g. Insights"
                />
              </Field>

              <Field label="Tags" hint="Comma-separated.">
                <input
                  className="input-base w-full"
                  value={form.tags}
                  onChange={(e) => set('tags', e.target.value)}
                  placeholder="algo-trading, options, risk"
                />
              </Field>

              <Field label="Author Name" span2>
                <input
                  className="input-base w-full"
                  value={form.author_name}
                  onChange={(e) => set('author_name', e.target.value)}
                  placeholder="Display name for author"
                />
              </Field>

              {/* Images section */}
              <div className="col-span-2 border-t border-bg-border/50 pt-3 mt-1">
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-3">Images</p>
              </div>

              <div className="col-span-2">
                <label className="block text-txt-muted mb-1">Featured Image URL</label>
                <div className="flex gap-2 items-center">
                  <input
                    className="input-base flex-1 font-mono text-xs"
                    value={form.featured_image_url}
                    onChange={(e) => set('featured_image_url', e.target.value)}
                    placeholder="https://..."
                  />
                  <button type="button"
                    className="btn-ghost text-xs px-2.5 py-1.5 border border-bg-border shrink-0"
                    onClick={() => featuredImgRef.current?.click()}
                    disabled={uploadingImage !== null}
                  >
                    {uploadingImage === 'featured' ? 'Uploading…' : 'Upload'}
                  </button>
                  <input ref={featuredImgRef} type="file"
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files?.[0], 'featured')} />
                </div>
                {form.featured_image_url && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <img src={imagePreviewSrc(form.featured_image_url)} alt=""
                      className="w-14 h-14 object-cover rounded border border-bg-border"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                    {imageUploadMeta.featured && (
                      <span className="text-2xs text-txt-muted font-mono">
                        {imageUploadMeta.featured.width}×{imageUploadMeta.featured.height}
                        {' · '}{Math.round(imageUploadMeta.featured.size_bytes / 1024)} KB
                      </span>
                    )}
                  </div>
                )}
                {imageUploadWarning.featured && (
                  <p className="mt-0.5 text-2xs text-warn">{imageUploadWarning.featured}</p>
                )}
                <p className="mt-0.5 text-2xs text-txt-muted">Use https:// URL or upload a JPG/PNG/WebP file.</p>
              </div>

              <Field label="Featured Image Alt" span2>
                <input
                  className="input-base w-full"
                  value={form.featured_image_alt}
                  onChange={(e) => set('featured_image_alt', e.target.value)}
                  placeholder="Describe the image for accessibility"
                />
              </Field>

              <div className="col-span-2">
                <label className="block text-txt-muted mb-1">OG Image URL</label>
                <div className="flex gap-2 items-center">
                  <input
                    className="input-base flex-1 font-mono text-xs"
                    value={form.og_image_url}
                    onChange={(e) => set('og_image_url', e.target.value)}
                    placeholder="https://..."
                  />
                  <button type="button"
                    className="btn-ghost text-xs px-2.5 py-1.5 border border-bg-border shrink-0"
                    onClick={() => ogImgRef.current?.click()}
                    disabled={uploadingImage !== null}
                  >
                    {uploadingImage === 'og' ? 'Uploading…' : 'Upload'}
                  </button>
                  <input ref={ogImgRef} type="file"
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files?.[0], 'og')} />
                </div>
                {form.og_image_url && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <img src={imagePreviewSrc(form.og_image_url)} alt=""
                      className="w-14 h-14 object-cover rounded border border-bg-border"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                    {imageUploadMeta.og && (
                      <span className="text-2xs text-txt-muted font-mono">
                        {imageUploadMeta.og.width}×{imageUploadMeta.og.height}
                        {' · '}{Math.round(imageUploadMeta.og.size_bytes / 1024)} KB
                      </span>
                    )}
                  </div>
                )}
                {imageUploadWarning.og && (
                  <p className="mt-0.5 text-2xs text-warn">{imageUploadWarning.og}</p>
                )}
                <p className="mt-0.5 text-2xs text-txt-muted">OG image is optimized to 1200×630.</p>
              </div>

              {/* SEO section */}
              <div className="col-span-2 border-t border-bg-border/50 pt-3 mt-1">
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-3">SEO</p>
              </div>

              <Field label="Meta Title" span2>
                <input
                  className="input-base w-full"
                  value={form.meta_title}
                  onChange={(e) => set('meta_title', e.target.value)}
                  placeholder="Defaults to article title if blank"
                />
              </Field>

              <Field label="Meta Description" span2>
                <textarea
                  className="input-base w-full resize-none"
                  rows={2}
                  value={form.meta_description}
                  onChange={(e) => set('meta_description', e.target.value)}
                  placeholder="150–160 characters recommended"
                />
              </Field>

              <Field label="Canonical URL" span2>
                <input
                  className="input-base w-full font-mono"
                  value={form.canonical_url}
                  onChange={(e) => set('canonical_url', e.target.value)}
                  placeholder="https://..."
                />
              </Field>

              {/* Article metadata */}
              {!isNew && article && (
                <div className="col-span-2 border-t border-bg-border/50 pt-3 mt-1 space-y-1.5">
                  <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Info</p>
                  <p className="text-2xs text-txt-muted">
                    Public rendering will be connected in W2D.
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-2xs text-txt-muted font-mono">
                    <span>Created: {fmtDateTime(article.created_at)}</span>
                    <span>Updated: {fmtDateTime(article.updated_at)}</span>
                    {article.published_at && (
                      <span>Published: {fmtDateTime(article.published_at)}</span>
                    )}
                    <span className="break-all">ID: {article.id}</span>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-bg-border shrink-0">
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : isNew ? 'Create Draft' : 'Save'}
          </button>
          <button type="button" className="btn-ghost text-xs" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminWebsiteArticles() {
  const [qInput, setQInput]               = useState('');
  const [q, setQ]                         = useState('');
  const [statusFilter, setStatusFilter]   = useState('ALL');
  const [categoryInput, setCategoryInput] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagInput, setTagInput]           = useState('');
  const [tagFilter, setTagFilter]         = useState('');
  const [page, setPage]                   = useState(0);
  const [drawerArticleId, setDrawerArticleId] = useState(null);

  const params = {
    limit:  PAGE_SIZE,
    offset: page * PAGE_SIZE,
    ...(q              ? { q }                : {}),
    ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
    ...(categoryFilter ? { category: categoryFilter } : {}),
    ...(tagFilter      ? { tag: tagFilter }    : {}),
  };

  const { data, isLoading, error, refetch } = useWebsiteArticles(params);
  const articles   = data?.items ?? (Array.isArray(data) ? data : []);
  const total      = data?.total ?? articles.length;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  function handleSearch() {
    setQ(qInput.trim());
    setCategoryFilter(categoryInput.trim());
    setTagFilter(tagInput.trim());
    setPage(0);
  }

  function handleClear() {
    setQInput('');         setQ('');
    setCategoryInput('');  setCategoryFilter('');
    setTagInput('');       setTagFilter('');
    setStatusFilter('ALL');
    setPage(0);
  }

  const hasFilters = q || statusFilter !== 'ALL' || categoryFilter || tagFilter;

  return (
    <div className="space-y-4 p-4">
      <SectionHeader
        title="Website Articles"
        actions={
          <div className="flex items-center gap-2">
            <button
              className="btn-ghost p-1.5 rounded-lg"
              onClick={() => refetch()}
              title="Refresh"
            >
              <RefreshCw size={13} />
            </button>
            <button
              className="btn-primary text-xs flex items-center gap-1.5"
              onClick={() => setDrawerArticleId('new')}
            >
              <Plus size={13} />
              Create Article
            </button>
          </div>
        }
      />

      {/* Filters */}
      <div className="panel p-3 space-y-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            className="input-base w-64 text-xs"
            placeholder="Search title, slug…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <input
            className="input-base w-32 text-xs"
            placeholder="Category"
            value={categoryInput}
            onChange={(e) => setCategoryInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <input
            className="input-base w-32 text-xs"
            placeholder="Tag"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button className="btn-primary text-xs" onClick={handleSearch}>Search</button>
          {hasFilters && (
            <button
              onClick={handleClear}
              className="text-2xs text-txt-muted hover:text-txt-secondary flex items-center gap-1"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-2xs font-mono text-txt-muted mr-1">Status:</span>
          {['ALL', ...ARTICLE_STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(0); }}
              className={cn(
                'px-2 py-0.5 rounded text-2xs font-mono transition-colors border',
                statusFilter === s
                  ? 'bg-amber/10 border-amber/30 text-amber'
                  : 'border-transparent text-txt-muted hover:text-txt-secondary',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="panel overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : error ? (
          <div className="px-4 py-6 text-xs text-bear">
            {error.message || 'Failed to load articles'}
          </div>
        ) : articles.length === 0 ? (
          <div className="px-4 py-10 text-xs text-txt-muted text-center">
            {hasFilters
              ? 'No articles match the current filters.'
              : 'No articles yet. Click Create Article to get started.'}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border">
                <th className="text-left py-2.5 px-3 text-2xs font-mono uppercase text-txt-muted whitespace-nowrap">
                  Created / Updated
                </th>
                <th className="text-left py-2.5 px-3 text-2xs font-mono uppercase text-txt-muted">
                  Title
                </th>
                <th className="text-left py-2.5 px-3 text-2xs font-mono uppercase text-txt-muted">
                  Slug
                </th>
                <th className="text-left py-2.5 px-3 text-2xs font-mono uppercase text-txt-muted">
                  Category / Tags
                </th>
                <th className="text-left py-2.5 px-3 text-2xs font-mono uppercase text-txt-muted">
                  Status
                </th>
                <th className="text-left py-2.5 px-3 text-2xs font-mono uppercase text-txt-muted whitespace-nowrap">
                  Published At
                </th>
                <th className="py-2.5 px-3" />
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr
                  key={article.id}
                  className="border-b border-bg-border/50 hover:bg-bg-hover cursor-pointer transition-colors"
                  onClick={() => setDrawerArticleId(article.id)}
                >
                  <td className="py-2.5 px-3 text-txt-muted font-mono whitespace-nowrap">
                    <div>{fmtDate(article.created_at)}</div>
                    {article.updated_at && article.updated_at !== article.created_at && (
                      <div className="text-2xs opacity-60">{fmtDate(article.updated_at)}</div>
                    )}
                  </td>
                  <td className="py-2.5 px-3 max-w-[200px]">
                    <div className="truncate font-medium text-txt-primary">{article.title}</div>
                    {article.excerpt && (
                      <div className="text-2xs text-txt-muted truncate mt-0.5">{article.excerpt}</div>
                    )}
                  </td>
                  <td className="py-2.5 px-3 max-w-[160px]">
                    <div className="truncate text-2xs font-mono text-txt-muted">
                      {article.slug || '—'}
                    </div>
                  </td>
                  <td className="py-2.5 px-3 max-w-[180px]">
                    {article.category && (
                      <div className="text-2xs font-mono text-info">{article.category}</div>
                    )}
                    {Array.isArray(article.tags) && article.tags.length > 0 && (
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="text-2xs px-1 rounded bg-bg-surface border border-bg-border text-txt-muted"
                          >
                            {tag}
                          </span>
                        ))}
                        {article.tags.length > 3 && (
                          <span className="text-2xs text-txt-muted">+{article.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                    {!article.category && (!article.tags || article.tags.length === 0) && (
                      <span className="text-txt-muted">—</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 whitespace-nowrap">
                    <StatusBadge status={article.status} />
                  </td>
                  <td className="py-2.5 px-3 text-2xs text-txt-muted font-mono whitespace-nowrap">
                    {article.published_at ? fmtDateTime(article.published_at) : '—'}
                  </td>
                  <td className="py-2.5 px-3">
                    <button
                      className="btn-ghost text-2xs px-2 py-1"
                      onClick={(e) => { e.stopPropagation(); setDrawerArticleId(article.id); }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-bg-border text-xs text-txt-muted">
            <span>Page {page + 1} of {totalPages} ({total} total)</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {drawerArticleId && (
        <ArticleDrawer
          articleId={drawerArticleId}
          onClose={() => setDrawerArticleId(null)}
          onCreated={(created) => setDrawerArticleId(created.id)}
          onDeleted={() => setDrawerArticleId(null)}
        />
      )}
    </div>
  );
}
